import {gql} from '@apollo/client';
import {css} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  ProcedureRunDataEntryMode,
  ProcedureType,
  StudyType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {ProcedureRunPlanConditionREST, updateProcedureRunPlan} from 'procedures/network/procedureRunPlanDAO';
import buildFullExperimentPlan, {
  deriveConfirmationMethodByParamVersionId,
} from 'procedures/planning/buildFullExperimentPlan';
import {
  expandPlanDataParameterValuesForRest,
  expandPlanMaterialInputValuesForRest,
} from 'procedures/planning/multiValuePlateMap';
import {ExperimentalInputs, FullExperimentPlan} from 'procedures/planning/ProcedurePlanTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {
  getFlowchartVersionIdForNavigation,
  getPowAgnosticProcedureFromFlowchartVersion,
  getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan,
} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {BenchlingState} from 'redux-stores/reduceApp';
import {
  ProcedureRunPlanDesignStateSummary,
  setProcedureRunPlanDesignState,
} from 'studies/redux/ProcedureRunPlanUIReduxActions';
import {ConditionGroup, createDefaultPlanConditions} from 'studies/typed/shared/useExperimentDesignState';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import legacyRunModal from 'ui-library/legacyRunModal';
import FlowchartSidebar from 'ui-library/reactflow/sidepanels/FlowchartSidebar';
import {withLoadingScreen} from 'util/Promises';

import {StudyInaccessibleContentBanner} from '../utils/ui/StudyQueryBanners';
import {
  MultiProcedureRunPlanStudyDesignTabContentsQuery,
  MultiProcedureRunPlanStudyDesignTabContentsQueryVariables,
  MultiProcedureRunPlanStudyDesignTabContentsQuery_procedureRunPlan_ProcedureRunPlanNode as ProcedureRunPlanNode,
} from './__generated/MultiProcedureRunPlanStudyDesignTabContentsQuery';
import EditProcedureModal, {EditProcedureModalMode, ProcedureChangeParams} from './EditProcedureModal';
import {prepareInitialExperimentalInputs} from './ExperimentalInputs';
import getStudyDesignFromProcedureRunPlan, {
  STUDY_DESIGN_FROM_PROCEDURE_RUN_PLAN_FRAGMENT,
} from './getStudyDesignFromProcedureRunPlan';
import {StudyOverviewContext} from './MultiProcedureRunAndPlanOverviewTabContents';
import {preserveProcedureInputsOnVersionUpdate} from './preserveProcedureInputsOnVersionUpdate';
import {
  collectPlanErrors,
  getConditionValidationTooltip,
  validateConditions,
} from './procedureRunStudyValidationUtils';
import PublishedProcedureVersionUpdateBanner from './PublishedProcedureVersionUpdateBanner';
import SelectedProcedureEditor from './SelectedProcedureEditor';
import StudyDesignTabSavingButtons, {StudyDesignTabEditState} from './StudyDesignTabSavingButtons';
import {
  ContextPanel,
  PARAMETER_VALUES_TAB_NAME,
  ParameterValuesTabContents,
  SETTINGS_TAB_NAME,
  SettingsTabContents,
  studyDesignTabStyles,
} from './StudyDesignTabSharedComponents';
import {useProcedureVersionUpdateCheck} from './useProcedureVersionUpdateCheck';
import {useStudyDesignState} from './useStudyDesignState';

// Indices for accessing typed values in the experiment plan
const MATERIAL_INDEX = 0;
const VALUE_INDEX = 0;

const AMOUNT_INDEX = 1;

function hasCompatiblePersistedMethodInputs(
  experimentalInputs: ExperimentalInputs,
  methodIds: Set<string>
): boolean {
  const persistedMethodIds = Object.keys(experimentalInputs);
  // Require an exact key-set match so that we never reuse persisted inputs
  // that are missing entries for current methods or contain obsolete ones.
  if (persistedMethodIds.length !== methodIds.size) {
    return false;
  }
  return persistedMethodIds.every((methodId) => methodIds.has(methodId));
}

function isCompatiblePersistedPlanDesignState(
  persistedPlanDesignState: ProcedureRunPlanDesignStateSummary | undefined,
  methodIds: Set<string>
): persistedPlanDesignState is ProcedureRunPlanDesignStateSummary {
  return (
    persistedPlanDesignState != null &&
    hasCompatiblePersistedMethodInputs(persistedPlanDesignState.experimentalInputs, methodIds)
  );
}

/**
 * Serializes experiment plan conditions to the procedure run PLAN REST format.
 * Note: This is different from serializeExperimentConditionsForNetwork which is for procedure RUNS.
 *
 * Plan REST API expects {value: TypedValue} for all types (data params, material inputs, equipment).
 * For material inputs, we nest both material and amount inside the value object.
 *
 * For parameters confirmed via plate map, the UI represents multiple setpoints as a single
 * multi-value TypedValue (ARRAY or multi-link BIOENTITY_LINK). This function expands those into
 * multiple `values[]` entries with one scalar TypedValue each, matching the contract of
 * serializeExperimentConditionsForNetwork and the backend planned-value upsert shape.
 */
export function serializePlanConditionsForNetwork(
  fullExperimentPlan: FullExperimentPlan,
  conditions: Array<ConditionGroup>
): Array<ProcedureRunPlanConditionREST> {
  const {replicatesPerCondition, namesPerCondition, descriptionsPerCondition, experimentPlanPerCondition} =
    fullExperimentPlan;

  return experimentPlanPerCondition.flatMap((methodPlans, i) => {
    const serializedMethodPlans = methodPlans.map((methodPlan) => ({
      methodVersionId: methodPlan.methodId,
      dataParameterValues: methodPlan.parameterValues.map((pv) => ({
        dataParameterVersionId: pv.sourceId,
        values: expandPlanDataParameterValuesForRest(pv.values[VALUE_INDEX]),
      })),
      materialInputValues: methodPlan.materialInputs.map((pv) => ({
        materialInputVersionId: pv.sourceId,
        values: expandPlanMaterialInputValuesForRest(pv.values[MATERIAL_INDEX], pv.values[AMOUNT_INDEX]),
      })),
      equipmentValues: methodPlan.equipmentInputs.map((pv) => ({
        equipmentVersionId: pv.sourceId,
        // Equipment parameters have allowMultiValueForPlateMap: false, so always a single value
        // entry. Coerce undefined to null so an unset cell serializes as an explicit null rather
        // than a missing key.
        values: [{value: {equipment: pv.values[VALUE_INDEX] ?? null}}],
      })),
    }));
    const conditionEntries = fanOutMethodPlansByConditionId(conditions[i], serializedMethodPlans);
    return conditionEntries.map(({id, templateMethodVersionId, methodPlan}) => ({
      ...(id != null ? {id} : {}),
      ...(templateMethodVersionId != null ? {templateMethodVersionId} : {}),
      name: namesPerCondition[i],
      description: descriptionsPerCondition[i],
      position: i,
      replicateInfos: Array.from({length: replicatesPerCondition[i]}, (_, j) => ({
        replicateNumber: j + 1,
      })),
      methodPlan,
    }));
  });
}

/**
 * Serializes preserved experimental inputs to REST format for procedure / version changes.
 *
 * Used when the plan's flowchart is being replaced (procedure swap or version update). The new
 * flowchart has a fresh set of PMDV ids, and may have a different number of PMDVs than the
 * existing condition rows reference, so there's no clean correspondence between the old per-PMDV
 * rows and the new flowchart's PMDVs. Rather than try to remap them in place, we emit one fresh
 * per-PMDV condition row per (condition, new PMDV) with no `id`. `update_procedure_run_plans_and_commit`
 * then deletes every existing row (none of their ids appear in the payload) and creates new rows
 * pointing at the new flowchart's PMDVs.
 *
 * Callers are responsible for procedure-change-specific cleanup of replicateInfos beyond
 * replicateNumber (e.g. clearing sourcePMEIIds, which reference PMEIs from the old flowchart
 * and are meaningless after the swap).
 *
 * @param preservedInputs - The experimental inputs with new method IDs and preserved values
 * @param conditions - The current conditions from the plan, used only for metadata (name,
 *   description, replicate count). `conditionIdsByPmdvId` is intentionally ignored because those
 *   ids reference rows that will be deleted.
 * @returns Array of conditions in REST format ready for the API
 */
function serializePreservedInputsForNetwork(
  preservedInputs: ExperimentalInputs,
  conditions: Array<ConditionGroup>
): Array<ProcedureRunPlanConditionREST> {
  return conditions.flatMap((condition, conditionIdx) =>
    Object.entries(preservedInputs).map(([methodId, inputsForMethod]) => {
      const valuesForCondition = inputsForMethod.values[conditionIdx];
      return {
        name: condition.name,
        description: condition.description,
        position: conditionIdx,
        templateMethodVersionId: methodId,
        replicateInfos: Array.from({length: condition.numReplicates}, (_, j) => ({
          replicateNumber: j + 1,
        })),
        methodPlan: {
          methodVersionId: methodId,
          dataParameterValues: inputsForMethod.ids.parameters.map((paramVersionId, paramIdx) => ({
            dataParameterVersionId: paramVersionId,
            values: expandPlanDataParameterValuesForRest(
              valuesForCondition?.parameters[paramIdx]?.[VALUE_INDEX]
            ),
          })),
          materialInputValues: inputsForMethod.ids.materialInputs.map((materialVersionId, materialIdx) => ({
            materialInputVersionId: materialVersionId,
            values: expandPlanMaterialInputValuesForRest(
              valuesForCondition?.materialInputs[materialIdx]?.[MATERIAL_INDEX],
              valuesForCondition?.materialInputs[materialIdx]?.[AMOUNT_INDEX]
            ),
          })),
          equipmentValues: inputsForMethod.ids.equipment.map((equipmentVersionId, equipmentIdx) => ({
            equipmentVersionId,
            // Equipment parameters have allowMultiValueForPlateMap: false, so always a single value
            // entry. Coerce undefined to null so an unset cell serializes as an explicit null rather
            // than a missing key.
            values: [
              {
                value: {
                  equipment: valuesForCondition?.equipment[equipmentIdx]?.[VALUE_INDEX] ?? null,
                },
              },
            ],
          })),
        },
      };
    })
  );
}

/**
 * Partitions serialized method plans into one or more condition payloads keyed by condition id.
 *
 * For persisted plans, each PMDV must map to its own condition id in `conditionIdsByPmdvId`, so this
 * returns one entry per method plan with that method's id and `templateMethodVersionId`.
 * For new unsaved conditions (no ids yet), this returns one id-less entry per method plan (i.e., per PMDV).
 */
function fanOutMethodPlansByConditionId(
  condition: ConditionGroup,
  methodPlans: Array<NonNullable<ProcedureRunPlanConditionREST['methodPlan']>>
): Array<{
  id?: string;
  templateMethodVersionId?: string;
  methodPlan: NonNullable<ProcedureRunPlanConditionREST['methodPlan']>;
}> {
  const conditionIdsByMethodVersionId = condition.conditionIdsByPmdvId;
  // An empty object indicates a new, unpersisted condition.
  if (Object.keys(conditionIdsByMethodVersionId).length === 0) {
    return methodPlans.map((methodPlan) => ({
      templateMethodVersionId: methodPlan.methodVersionId,
      methodPlan,
    }));
  }

  const seenConditionIds = new Set<string>();
  return methodPlans.map((methodPlan) => {
    const conditionId = conditionIdsByMethodVersionId[methodPlan.methodVersionId];
    assert(
      conditionId != null,
      `Expected condition ID for methodVersionId ${methodPlan.methodVersionId} in conditionIdsByPmdvId`
    );
    assert(
      !seenConditionIds.has(conditionId),
      `Expected one condition ID per PMDV within a ConditionGroup, got duplicate condition ID ${conditionId}`
    );
    seenConditionIds.add(conditionId);
    return {
      id: conditionId,
      templateMethodVersionId: methodPlan.methodVersionId,
      methodPlan,
    };
  });
}

export const MULTI_PROCEDURE_RUN_PLAN_STUDY_DESIGN_TAB_CONTENTS_QUERY: DocumentNode = gql`
  query MultiProcedureRunPlanStudyDesignTabContentsQuery($procedureRunPlanId: StrictString!)
  @skipAudit
  @engComponent(name: STUDIES) {
    procedureRunPlan(id: $procedureRunPlanId) {
      ...GetStudyDesignFromProcedureRunPlanGraphQL
      ... on ProcedureRunPlanNode {
        id
        templateProcedureFlowchartVersion {
          ... on ProcedureFlowchartVersionNode {
            id
            largestTableTypeSizes {
              tableKind
              size
            }
            procedureFlowchart {
              id
              parent {
                id
              }
            }
            migratedToFlowchartConfigVersionProxy {
              ... on ProcedureFlowchartConfigVersionProxyNode {
                id
              }
              ...InaccessibleInfoGraphQL
            }
          }
          ...InaccessibleInfoGraphQL
        }
        procedureFlowchartConfigVersionProxy {
          ... on ProcedureFlowchartConfigVersionProxyNode {
            id
            largestTableTypeSizes {
              tableKind
              size
            }
            procedure {
              id
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${STUDY_DESIGN_FROM_PROCEDURE_RUN_PLAN_FRAGMENT}
`;

function ProcedureRunPlanDesignTabContents(props: {
  procedureRunPlan: ProcedureRunPlanNode;
  studyType: StudyType;
  studyId: string;
  procedureType: ProcedureType;
  refetch: () => void;
}): JSX.Element {
  const dispatch = useDispatch();
  const {procedureRunPlan, procedureType} = props;
  const persistedPlanDesignState = useSelector(
    (state: BenchlingState) =>
      state.ui.procedureRunPlans.byStudyId[props.studyId]?.procedureRunPlanDesignStateByPlanId[
        procedureRunPlan.id
      ]
  );

  const {
    experimentalInputs: serverInitialExperimentalInputs,
    templateFlowchart,
    conditionGroups: serverInitialConditions,
  } = useMemo(() => getStudyDesignFromProcedureRunPlan(procedureRunPlan), [procedureRunPlan]);
  const templateMethodIds = useMemo(
    () => new Set(templateFlowchart.nodes.map((method) => method.id)),
    [templateFlowchart.nodes]
  );
  const compatiblePersistedPlanDesignState = isCompatiblePersistedPlanDesignState(
    persistedPlanDesignState,
    templateMethodIds
  )
    ? persistedPlanDesignState
    : undefined;
  const initialExperimentalInputs =
    compatiblePersistedPlanDesignState?.experimentalInputs ?? serverInitialExperimentalInputs;

  // Redux captures brand-new conditions before the first save assigns ids,
  // and nothing back-fills those ids into the snapshot afterwards. Refresh
  // missing ids from the server so subsequent saves update rows instead of
  // re-creating them. We match by position (index) because condition names
  // can be renamed between snapshots and are not unique-constrained.
  const initialConditions: Array<ConditionGroup> = useMemo(() => {
    if (compatiblePersistedPlanDesignState?.conditions == null) {
      return serverInitialConditions;
    }
    return compatiblePersistedPlanDesignState.conditions.map((persisted, index) => {
      if (Object.keys(persisted.conditionIdsByPmdvId).length > 0) {
        return persisted;
      }
      const serverCondition = serverInitialConditions[index];
      return serverCondition != null
        ? {...persisted, conditionIdsByPmdvId: serverCondition.conditionIdsByPmdvId}
        : persisted;
    });
  }, [compatiblePersistedPlanDesignState, serverInitialConditions]);

  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan(procedureRunPlan);
  assert(flowchartVersion != null && !isInaccessible(flowchartVersion), 'Expected flowchart version');
  const procedure = getPowAgnosticProcedureFromFlowchartVersion(flowchartVersion);
  const procedureId = procedure.id;

  // Check if a newer procedure version has been published
  const versionUpdateCheck = useProcedureVersionUpdateCheck(procedureId, flowchartVersion.id);
  const [isApplyingUpdate, setIsApplyingUpdate] = useState(false);
  // Get context to sync edit state with parent (for save-before-promote functionality)
  const {setIsInEditMode, setSavePlanIfInEditMode, setPlanErrors} = StudyOverviewContext.useContext();

  // Editable data entry mode state
  const [dataEntryMode, setDataEntryMode] = useState<ProcedureRunDataEntryMode>(
    compatiblePersistedPlanDesignState?.dataEntryMode ?? procedureRunPlan.dataEntryMode
  );
  const [initialDataEntryMode] = useState<ProcedureRunDataEntryMode>(procedureRunPlan.dataEntryMode);

  const {
    selectedMethodId,
    setSelectedMethodId,
    editState,
    setEditState,
    selectedDesignTabIndex,
    setSelectedDesignTabIndex,
    sidepanelName,
    setSidepanelName,
    conditions,
    setConditions,
    experimentalInputs,
    setExperimentalInputs,
    resetConditionsAndInputs,
    context,
    methodExecutionLimitState,
    editNumColsButtons,
  } = useStudyDesignState({
    isPlan: true,
    initialExperimentalInputs,
    templateFlowchart,
    initialConditions,
    createDefaultConditions: createDefaultPlanConditions,
    procedureType,
    flowchartVersion,
    dataEntryMode,
    procedureId,
    // Only restore edit mode from a compatible persisted state. When the persisted
    // state is incompatible (e.g., template changed), start in saved mode rather than
    // falling back to context isInEditMode which may have been set from stale state.
    initialEditMode: compatiblePersistedPlanDesignState?.isInEditMode ?? false,
  });
  // Build a map from method ID to method name for error categorization
  const methodIdToName = useMemo(
    () => Object.fromEntries(templateFlowchart.nodes.map((node) => [node.id, node.name])),
    [templateFlowchart.nodes]
  );

  // Propagate validation errors to the parent context so the header can display
  // the error badge and disable the Create button
  const conditionsText = getProcedureRunConditionTextForProcedureType(procedureType, {plural: true});
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.LOWER,
  });
  useEffect(() => {
    const errors = collectPlanErrors({
      evaluatedValues: context.evaluatedValues,
      methodExecutionLimitState,
      conditions,
      methodIdToName,
      conditionsText,
      hasNewerVersion: versionUpdateCheck.hasNewerVersion,
      procedureString,
    });

    setPlanErrors(errors);
  }, [
    context.evaluatedValues,
    conditionsText,
    setPlanErrors,
    methodExecutionLimitState,
    conditions,
    methodIdToName,
    versionUpdateCheck.hasNewerVersion,
    procedureString,
  ]);

  // Clear plan errors only on unmount so we don't briefly flash an empty state
  // between dependency-driven re-runs of the effect above.
  useOnMount(() => () => {
    setPlanErrors([]);
  });

  // Create a stable save function reference that can be called from parent
  const handleSaveForPromotion = useCallback(async () => {
    if (editState !== StudyDesignTabEditState.EDITING) {
      return;
    }

    setEditState(StudyDesignTabEditState.SAVING);
    assert(conditions != null, 'Conditions should not be null for plan');
    assert(experimentalInputs != null, 'Experimental inputs should not be null for plan');
    assert(context != null, 'Expected context to be non-null');

    try {
      // Build full experiment plan from current state
      const replicatesPerCondition = conditions.map((condition) => condition.numReplicates);
      const namesPerCondition = conditions.map((condition) => condition.name);
      const descriptionsPerCondition = conditions.map((condition) => condition.description);
      const experimentPlan = buildFullExperimentPlan({
        replicatesPerCondition,
        namesPerCondition,
        descriptionsPerCondition,
        numConditions: conditions.length,
        experimentalInputs,
        evaluatedValues: context.evaluatedValues,
      });

      // Serialize to plan-specific REST format (different from procedure runs)
      const conditionsForUpdate = serializePlanConditionsForNetwork(experimentPlan, conditions);

      await updateProcedureRunPlan({
        id: procedureRunPlan.id,
        studyId: props.studyId,
        experimentConditions: conditionsForUpdate,
        confirmationMethodByParamVersionId: experimentPlan.confirmationMethodByParamVersionId,
        dataEntryMode,
      });

      // Refetch to get updated data
      props.refetch();
      setEditState(StudyDesignTabEditState.SAVED);
    } catch (error) {
      // On error, go back to editing state so user can retry
      setEditState(StudyDesignTabEditState.EDITING);
      throw error;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editState, conditions, experimentalInputs, context, procedureRunPlan.id, props.studyId, dataEntryMode]);

  useEffect(() => {
    setIsInEditMode(editState === StudyDesignTabEditState.EDITING);
  }, [editState, setIsInEditMode]);

  useEffect(() => {
    dispatch(
      setProcedureRunPlanDesignState(props.studyId, procedureRunPlan.id, {
        conditions,
        experimentalInputs,
        dataEntryMode,
        isInEditMode: editState === StudyDesignTabEditState.EDITING,
      })
    );
  }, [
    conditions,
    dataEntryMode,
    dispatch,
    editState,
    experimentalInputs,
    procedureRunPlan.id,
    props.studyId,
  ]);

  useEffect(() => {
    setSavePlanIfInEditMode(handleSaveForPromotion);
    return () => {
      setSavePlanIfInEditMode(null);
    };
  }, [handleSaveForPromotion, setSavePlanIfInEditMode]);

  function handleCancel(): void {
    setEditState(StudyDesignTabEditState.SAVED);
    // Use resetConditionsAndInputs to atomically reset both conditions and experimental inputs
    // This avoids stale closure issues when calling setConditions and setExperimentalInputs separately
    resetConditionsAndInputs(serverInitialConditions, serverInitialExperimentalInputs);
    setDataEntryMode(initialDataEntryMode);
  }

  /**
   * Handles changing the procedure on a procedure run plan.
   * Updates the plan with the new flowchart version while preserving condition metadata
   * (names, descriptions, replicates) and resetting planned values to the selected procedure's defaults.
   */
  async function handleChangeProcedure(params: ProcedureChangeParams): Promise<void> {
    const defaultInputsForNewProcedure = prepareInitialExperimentalInputs(
      conditions.length,
      params.procedure.methods
    );
    const confirmationMethodByParamVersionId = deriveConfirmationMethodByParamVersionId(
      defaultInputsForNewProcedure
    );
    const conditionsForUpdate = serializePreservedInputsForNetwork(
      defaultInputsForNewProcedure,
      conditions
    ).map((condition) => ({
      ...condition,
      // Unit operation links are specific to the old procedure and must be cleared.
      replicateInfos: condition.replicateInfos.map((replicateInfo) => ({
        ...replicateInfo,
        sourcePMEIIds: [],
      })),
    }));

    await updateProcedureRunPlan({
      id: procedureRunPlan.id,
      studyId: props.studyId,
      // XOR validation handled by EditProcedureModal - only one of these will be set
      templateProcedureFlowchartVersionId: params.templateProcedureFlowchartVersionId,
      workflowFlowchartConfigVersionId: params.workflowFlowchartConfigVersionId,
      experimentConditions: conditionsForUpdate,
      confirmationMethodByParamVersionId,
    });

    // Exit edit mode when changing the procedure template since the new template
    // may have different methods and parameters that require fresh editing
    setEditState(StudyDesignTabEditState.SAVED);

    props.refetch();
  }

  /**
   * Compares the current and new procedure versions and preserves inputs for
   * parameters/materials/equipment whose underlying procedure elements have
   * matching stable parameter IDs (as determined by preserveProcedureInputsOnVersionUpdate).
   */
  async function handleApplyProcedureUpdate(): Promise<void> {
    if (
      isApplyingUpdate ||
      !versionUpdateCheck.hasNewerVersion ||
      !versionUpdateCheck.latestPublishedVersion
    ) {
      return;
    }

    setIsApplyingUpdate(true);

    try {
      const newFlowchart = versionUpdateCheck.latestPublishedVersion;
      const preservedInputs = preserveProcedureInputsOnVersionUpdate(
        experimentalInputs,
        templateFlowchart,
        newFlowchart,
        conditions.length
      );
      const conditionsForUpdate = serializePreservedInputsForNetwork(preservedInputs, conditions).map(
        (condition) => ({
          ...condition,
          // Unit operation links reference PMDVs from the previous flowchart version and are
          // meaningless against the new version's PMDVs, so clear them.
          replicateInfos: condition.replicateInfos.map((replicateInfo) => ({
            ...replicateInfo,
            sourcePMEIIds: [],
          })),
        })
      );

      await withLoadingScreen(
        updateProcedureRunPlan({
          id: procedureRunPlan.id,
          studyId: props.studyId,
          templateProcedureFlowchartVersionId: versionUpdateCheck.isPoW ? undefined : newFlowchart.id,
          workflowFlowchartConfigVersionId: versionUpdateCheck.isPoW ? newFlowchart.id : undefined,
          experimentConditions: conditionsForUpdate,
        }),
        `Updating to latest ${procedureString} version...`
      );

      notifications.success(`Updated to latest ${procedureString} version.`);
      props.refetch();
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (error) {
      notifications.error(`Failed to apply ${procedureString} update. Please try again.`);
    } finally {
      setIsApplyingUpdate(false);
    }
  }

  async function handleOpenApplyUpdateModal(): Promise<void> {
    if (!versionUpdateCheck.hasNewerVersion || !versionUpdateCheck.latestPublishedVersion) {
      return;
    }
    if (isInaccessible(flowchartVersion)) {
      return;
    }

    const currentFlowchartVersionId = getFlowchartVersionIdForNavigation(flowchartVersion);

    await legacyRunModal(EditProcedureModal, {
      mode: EditProcedureModalMode.VERSION_UPDATE,
      procedureId,
      flowchartVersionId: currentFlowchartVersionId,
      outdatedFlowchartVersionId: currentFlowchartVersionId,
      currentFlowchartVersionId: versionUpdateCheck.latestPublishedVersion.id,
      studyType: props.studyType,
      procedureType,
      onApplyVersionUpdate: handleApplyProcedureUpdate,
    });
  }

  return selectedMethodId == null ? (
    <div className={css(studyDesignTabStyles.bufferTabWrapper)}>
      {versionUpdateCheck.hasNewerVersion && (
        <PublishedProcedureVersionUpdateBanner
          procedureType={procedureType}
          onOpenUpdateModal={handleOpenApplyUpdateModal}
        />
      )}
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text={`No ${getProcedureUIString(ProceduresStringKeys.METHOD, procedureType, {
          stringCase: StringCase.LOWER,
          plural: true,
        })} have been configured for this ${getProcedureUIString(
          ProceduresStringKeys.PROCEDURE,
          procedureType,
          {stringCase: StringCase.LOWER}
        )}.`}
      />
    </div>
  ) : (
    <div
      className={css(studyDesignTabStyles.designBackground)}
      {...getTestProps({component: 'ProcedureRunPlanDesignTabContents', element: 'root'})}
    >
      <div className={css(studyDesignTabStyles.header)}>
        <div className={css(studyDesignTabStyles.tabTitle)}>Design</div>
        <div className={css(studyDesignTabStyles.bannerAndButtons)}>
          {versionUpdateCheck.hasNewerVersion && (
            <PublishedProcedureVersionUpdateBanner
              procedureType={procedureType}
              onOpenUpdateModal={handleOpenApplyUpdateModal}
            />
          )}
          <StudyDesignTabSavingButtons
            onCancel={handleCancel}
            onEdit={() => {
              setEditState(StudyDesignTabEditState.EDITING);
            }}
            onSave={handleSaveForPromotion}
            editState={editState}
            isSaveDisabled={!validateConditions(conditions)}
            saveDisabledTooltip={getConditionValidationTooltip(conditions) ?? undefined}
          />
        </div>
      </div>
      <div className={css(studyDesignTabStyles.leftSidepanelContainer)}>
        <FlowchartSidebar
          icons={[
            {
              name: 'note-table',
              onClick: () => {
                setSidepanelName(PARAMETER_VALUES_TAB_NAME);
              },
            },
            {
              name: 'settings',
              onClick: () => {
                setSidepanelName(SETTINGS_TAB_NAME);
              },
            },
          ]}
          selectedIconIndex={selectedDesignTabIndex}
          setSelectedIconIndex={setSelectedDesignTabIndex}
        />
        <ContextPanel
          sidepanelName={sidepanelName}
          sidepanelHeaderContent={
            selectedDesignTabIndex === 0 && editState === StudyDesignTabEditState.EDITING
              ? (editNumColsButtons as JSX.Element)
              : undefined
          }
        >
          {selectedDesignTabIndex === 0 ? (
            <ParameterValuesTabContents
              methods={templateFlowchart.nodes}
              procedureFlowchart={templateFlowchart}
              selectedMethodId={selectedMethodId}
              onChangeSelectedMethod={setSelectedMethodId}
              procedureType={procedureType}
              context={context}
            />
          ) : (
            <>
              <SettingsTabContents
                conditions={conditions}
                setConditions={setConditions}
                selectedMethodId={selectedMethodId}
                experimentalInputs={experimentalInputs}
                setExperimentalInputs={setExperimentalInputs}
                locked={editState !== StudyDesignTabEditState.EDITING}
                procedureType={procedureType}
                methodExecutionLimitState={methodExecutionLimitState}
                studyType={props.studyType}
                dataEntryMode={dataEntryMode}
                estimateResult={{stale: false, executionPlan: null}}
                setDataEntryMode={setDataEntryMode}
                isPlan={true}
              />
              <SelectedProcedureEditor
                isEditable={editState === StudyDesignTabEditState.EDITING}
                procedureId={procedureId}
                flowchartVersionId={getFlowchartVersionIdForNavigation(flowchartVersion)}
                procedureType={procedureType}
                studyType={props.studyType}
                onProcedureChange={handleChangeProcedure}
              />
            </>
          )}
        </ContextPanel>
      </div>
    </div>
  );
}

export default function QueryingProcedureRunPlanDesignTabContents({
  procedureRunPlanId,
  studyType,
  studyId,
  procedureType,
}: {
  procedureRunPlanId: string;
  studyType: StudyType;
  studyId: string;
  procedureType: ProcedureType;
}): JSX.Element {
  const result = useBenchlingQuery<
    MultiProcedureRunPlanStudyDesignTabContentsQuery,
    MultiProcedureRunPlanStudyDesignTabContentsQueryVariables
  >(MULTI_PROCEDURE_RUN_PLAN_STUDY_DESIGN_TAB_CONTENTS_QUERY, {variables: {procedureRunPlanId}});

  if (result.type !== QueryResultType.SUCCESS) {
    return <CenteredHorizontalThrobber />;
  }

  const {data, refetch} = result;
  if (isInaccessible(data.procedureRunPlan)) {
    return (
      <StudyInaccessibleContentBanner
        inaccessibleInfo={data.procedureRunPlan}
        modelType={ModelTypeConstants.PROCEDURE_RUN_PLAN}
        displayErrorText="This procedure run plan is no longer accessible."
      />
    );
  }

  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan(data.procedureRunPlan);
  if (isInaccessible(flowchartVersion)) {
    return (
      <StudyInaccessibleContentBanner
        inaccessibleInfo={flowchartVersion}
        modelType={ModelTypeConstants.PROCEDURE}
        displayErrorText="The design of this procedure run plan is not accessible."
      />
    );
  }

  return (
    <ProcedureRunPlanDesignTabContents
      procedureRunPlan={data.procedureRunPlan}
      studyType={studyType}
      studyId={studyId}
      procedureType={procedureType}
      refetch={refetch}
    />
  );
}
